/** The served sections of the Admin place - **EXP-22**, as the cards of five of its tabs (ADR-0212).
 *
 * Eight tabs now (`adminTabs.ts`, `Admin.tsx`): this file is the card list a section tab draws, with
 * the tallies over what the endpoint served. It was the whole Administration screen.
 *
 * (EXP-22, as it was first written:)
 *
 * > *"Organize workspace, people, routes, identity, data governance, methodology, integrations
 * > and audit."*
 *
 * Eight headings, and the product has all of one of them working end to end. The temptation is to
 * ship the one and leave the seven out; the reason not to is that **this is the screen somebody
 * opens to find out what the product can be made to do.** An administrator who looks for a
 * retention setting and finds no heading concludes there is nothing to set. One who finds a
 * heading saying retention is three years, anchored to the period a document covers and
 * unchangeable, has learned something accurate about what they bought.
 *
 * **This sentence used to end "and that there is no erasure path at all", which was false from
 * 2026-10-04** — `identity/erasure.py`, `POST /members/{user_id}/erase` and an "Erase this person"
 * control on `/people`, the screen this one links to. It was served to the customer as settled
 * fact, in three places, and an integration test asserted the words rather than the property, so
 * the only thing that could break it was somebody rewording it. The act is built; the process
 * around it — request intake, verifying who is asking, a response clock — is not, and the setting
 * now says which is which.
 *
 * **And that correction was itself overtaken on 2026-10-23 (ADR-0081, option A).** The "Erase this
 * person" control on `/people` and the served `POST /members/{user_id}/erase` no longer erase:
 * the route answers 409 `company_erasure_unavailable` to any local target, because the operation
 * anonymises a *global* identity that another company may share. The act still exists as
 * `identity/erasure.py`, a separately trusted owner service; the process around it — request
 * handling, verifying who is asking, a response clock — is still not built, and ADR-0077's intake
 * records a receipt only. This screen holds none of those sentences: it renders the served
 * "Erasure" row verbatim, and `Settings.test.tsx` fails if it authors its own. The legal half is
 * counsel's (B-02) and is stated nowhere here.
 *
 * So it is the same rule the Reports screen follows for a disclosure EmitIQ cannot produce: a line
 * that is not supported still gets a **row**.
 *
 * **Nothing here is typed into the frontend.** Every value arrives from the endpoint, which reads
 * it from the code that enforces it — the session timeout from `sessions.IDLE_TIMEOUT`, retention
 * from `RETENTION_YEARS`, the residency policy from the approval record. A settings screen that
 * restated a policy would eventually disagree with the product, and the disagreeing copy would be
 * the one a customer is shown.
 */

import { Link } from "@tanstack/react-router";

import { useAdministration } from "../api/queries";
import type { AdminSection } from "../api/queries";
import { adminStateWords, internalReferences, sectionSentence, settingWords } from "../lib/customerWords";
import { MfaPolicySettings } from "./MfaPolicy";
import own from "./AdminSections.module.css";

/** The four states, in the words a customer reads (`lib/customerWords.ts` holds the words: sentence case,
 * no "built").
 *
 * `none_yet` and `not_built` are kept apart deliberately - the same distinction the source census
 * draws between `pending` and `missing`. One is a state and the other is a gap.
 */
const STATES: Record<string, { label: string; tone?: "settled" | "notBuilt" }> = {
  settled: { label: adminStateWords("settled"), tone: "settled" },
  read_only: { label: adminStateWords("read_only") },
  not_built: { label: adminStateWords("not_built"), tone: "notBuilt" },
  none_yet: { label: adminStateWords("none_yet") },
};

function cls(...names: Array<string | false | undefined>): string {
  return names.filter(Boolean).join(" ");
}

/** One served section, as a group of rows in its tab's one table (ADR-0212: a place has one table).
 * The group's first row is the heading, its state and the way into it; then the statement as the
 * endpoint wrote it, the blocker if it names one, and a row per setting (what is set, to what, and
 * the note on it). A group is a `tbody` named by its heading, so a reader of the table hears the
 * section before its rows. */
export function Section({ section }: { section: AdminSection }) {
  const state = STATES[section.state] ?? { label: adminStateWords(section.state) };
  return (
    <tbody
      className={cls(own.section)}
      data-section={section.key}
      data-state={section.state}
      aria-labelledby={`administration-section-${section.key}`}
    >
      <tr className={cls(own.groupRow)}>
        <th colSpan={3} scope="colgroup" className={cls(own.groupHead)}>
          <span className={cls(own.head)}>
            <h3 className={cls(own.title)} id={`administration-section-${section.key}`}>{section.title}</h3>
            <span className={cls(own.state, state.tone && own[state.tone])}>{state.label}</span>
            {section.route ? (
              <Link to={section.route} className={cls(own.link)}>
                Open
              </Link>
            ) : null}
          </span>
        </th>
      </tr>
      <tr>
        <td colSpan={3} className={cls(own.noteCell)}>
          <p className={cls(own.summary)}>{sectionSentence(section.summary)}</p>
          {/* **Named, not shrugged at.** "Not built" on its own leaves the reader with no next step;
              with the blocker named, the answer to "when?" is a decision somebody can go and read. */}
          {section.blocked_by ? <p className={cls(own.blocked)}>{sectionSentence(section.blocked_by)}</p> : null}
        </td>
      </tr>
      {section.items.map((item) => {
        // The row as the customer reads it: the served value and note, with the customer-words table's
        // wording where the served one names a decision record or a build state (`settingWords`).
        const words = settingWords(section.key, item.label, item);
        return (
          /* **`data-item` so a check can find a row by what it is about.** The browser gate and the
             tests have to assert that a specific statement reached the screen, and the only
             alternative is searching rendered text - which is the trap this screen is full of: every
             honest sentence here names the capability it is denying, so "tagged" or "erasure" appears
             in the DOM whether the row is wired or not. The label is the section's own key for a row,
             the same way `data-section` is for a section. */
          <tr
            key={item.label}
            className={cls(own.item)}
            data-item={item.label}
            data-references={internalReferences(item.detail ?? "").join(" ")}
          >
            <th scope="row" className={cls(own.label)}>{item.label}</th>
            <td className={cls(own.value, /^not /i.test(item.value) && own.unavailable)} data-served-value={item.value}>
              {words.value}
            </td>
            <td className={cls(own.detail)}>{words.detail}</td>
          </tr>
        );
      })}
      {section.key === "identity" ? (
        <tr>
          <td colSpan={3} className={cls(own.noteCell)}>
            <MfaPolicySettings />
          </td>
        </tr>
      ) : null}
    </tbody>
  );
}

/** The tallies of what the endpoint served, and nothing the screen believes. Present only once the
 * read has succeeded: a tile counting sections from a failed read would print four zeros, which is
 * the defect `return data ?? []` was. */
export function tallies(
  administration: ReturnType<typeof useAdministration>,
  /** Count only the sections drawn on this tab. The same four numbers on every tab were a strip that
   * did not change when the tab did, so it read as decoration and not as a count of what is here. */
  inTab?: (sectionKey: string) => boolean,
) {
  const data = administration.data;
  const tally = (state: string) =>
    data?.sections.filter((section) => section.state === state && (inTab?.(section.key) ?? true)).length;
  return {
    inUse: data ? tally("settled") : undefined,
    inForce: data ? tally("read_only") : undefined,
    nothingYet: data ? tally("none_yet") : undefined,
    notBuilt: data ? tally("not_built") : undefined,
  };
}
